/*
 * Carbon and hydrogen counts straight from a SMILES string.
 *
 * Used by the submit page to sanity-check a mechanism while it is being typed
 * (see mechanismWarnings.js). It has to run on molecules the user has just
 * invented, so it cannot ask the backend for a formula - hence this small
 * parser instead of RDKit.
 *
 * Only what the check needs is modelled: atoms, bond orders, branches and ring
 * closures. Anything it does not understand returns null, which the callers
 * treat as "cannot tell" and skip - a missed warning is much better than a
 * wrong one.
 */

// Atoms that may be written without brackets, and their default valences in the
// order they are tried. Everything else has to be bracketed, and bracketed atoms
// always state their hydrogens explicitly.
const DEFAULT_VALENCES = {
  B: [3],
  C: [4],
  N: [3, 5],
  O: [2],
  P: [3, 5],
  S: [2, 4, 6],
  F: [1],
  CL: [1],
  BR: [1],
  I: [1],
};

const BOND_ORDERS = { "-": 1, "=": 2, "#": 3, "$": 4, ":": 1, "/": 1, "\\": 1 };

/* Longest organic-subset symbol at position i, or null. */
function readOrganicSymbol(smiles, i) {
  const two = smiles.slice(i, i + 2);
  if (two === "Cl" || two === "Br") return two;
  const one = smiles[i];
  if ("BCNOPSFI".includes(one)) return one;
  if ("bcnops".includes(one)) return one;
  return null;
}

/* Contents of a bracket atom: element symbol and the hydrogen count it states. */
function parseBracketAtom(body) {
  let i = 0;
  while (i < body.length && /[0-9]/.test(body[i])) i++; // isotope
  let symbol = "";
  if (i < body.length && /[A-Za-z*]/.test(body[i])) {
    symbol = body[i];
    i++;
    if (i < body.length && /[a-z]/.test(body[i]) && /[A-Z]/.test(symbol)) {
      // Two-letter element (Se, Cl, ...); "cn" style aromatic pairs do not exist.
      symbol += body[i];
      i++;
    }
  }
  if (!symbol) return null;
  while (i < body.length && body[i] === "@") i++; // chirality marker(s)
  // Chirality classes such as @TH1 / @AL2 sit between the marker and the H count.
  while (i < body.length && /[A-Z]/.test(body[i]) && body[i] !== "H") {
    i++;
    while (i < body.length && /[0-9]/.test(body[i])) i++;
  }
  let hydrogens = 0;
  if (body[i] === "H") {
    i++;
    let digits = "";
    while (i < body.length && /[0-9]/.test(body[i])) {
      digits += body[i];
      i++;
    }
    hydrogens = digits === "" ? 1 : parseInt(digits, 10);
  }
  return { symbol, hydrogens, aromatic: /^[a-z]/.test(symbol) };
}

/**
 * @returns {{carbon: number, hydrogen: number}|null} null when the SMILES cannot
 *          be read with confidence.
 */
export default function countAtoms(smiles) {
  if (typeof smiles !== "string") return null;
  const text = smiles.trim();
  if (text === "") return null;
  if (text.includes("*")) return null; // wildcard atom - carbon count is unknowable

  const atoms = []; // { symbol, aromatic, bracket, hydrogens, bonds }
  const branchStack = [];
  const ringBonds = new Map();
  let previous = -1;
  let pendingBond = null; // explicit bond order waiting for its second atom
  let broken = false;

  const connect = (a, b, order) => {
    atoms[a].bonds += order;
    atoms[b].bonds += order;
  };

  const addAtom = (atom) => {
    atoms.push({ ...atom, bonds: 0 });
    const index = atoms.length - 1;
    if (previous >= 0) {
      connect(previous, index, pendingBond === null ? 1 : pendingBond);
    }
    pendingBond = null;
    previous = index;
  };

  let i = 0;
  while (i < text.length && !broken) {
    const char = text[i];

    if (char === "[") {
      const end = text.indexOf("]", i);
      if (end < 0) return null;
      const atom = parseBracketAtom(text.slice(i + 1, end));
      if (!atom) return null;
      addAtom({ symbol: atom.symbol, aromatic: atom.aromatic, bracket: true, hydrogens: atom.hydrogens });
      i = end + 1;
      continue;
    }

    const organic = readOrganicSymbol(text, i);
    if (organic) {
      addAtom({ symbol: organic, aromatic: /^[a-z]/.test(organic), bracket: false, hydrogens: 0 });
      i += organic.length;
      continue;
    }

    if (char === "(") {
      if (previous < 0) return null;
      branchStack.push(previous);
      i++;
      continue;
    }
    if (char === ")") {
      if (branchStack.length === 0) return null;
      previous = branchStack.pop();
      pendingBond = null;
      i++;
      continue;
    }
    if (char === ".") {
      previous = -1;
      pendingBond = null;
      i++;
      continue;
    }
    if (char in BOND_ORDERS) {
      pendingBond = BOND_ORDERS[char];
      i++;
      continue;
    }
    if (char === "%" || /[0-9]/.test(char)) {
      let label;
      if (char === "%") {
        label = text.slice(i + 1, i + 3);
        if (!/^[0-9]{2}$/.test(label)) return null;
        i += 3;
      } else {
        label = char;
        i += 1;
      }
      if (previous < 0) return null;
      const open = ringBonds.get(label);
      if (open === undefined) {
        ringBonds.set(label, { atom: previous, order: pendingBond });
      } else {
        // Either end may carry the bond symbol; a specified order wins over the default.
        connect(open.atom, previous, open.order ?? pendingBond ?? 1);
        ringBonds.delete(label);
      }
      pendingBond = null;
      continue;
    }
    // Anything else (unsupported syntax) - do not guess.
    broken = true;
  }

  // Half-typed input (open branch, dangling bond, unclosed ring) is not an error
  // worth reporting - it just means the counts are not trustworthy yet.
  if (broken || ringBonds.size > 0 || branchStack.length > 0 || pendingBond !== null) return null;
  if (atoms.length === 0) return null;

  let carbon = 0;
  let hydrogen = 0;
  for (const atom of atoms) {
    const element = atom.symbol.toUpperCase();
    if (element === "C") carbon++;
    if (element === "H") {
      // A hydrogen written as its own atom, e.g. the leading [H] of [H][C@@]12CC...
      hydrogen++;
      continue;
    }
    if (atom.bracket) {
      hydrogen += atom.hydrogens;
      continue;
    }
    // An aromatic atom spends one more bond inside the ring system than the
    // single-bond count above credits it with.
    const used = atom.bonds + (atom.aromatic ? 1 : 0);
    const valences = DEFAULT_VALENCES[element];
    if (!valences) return null;
    const target = valences.find((valence) => valence >= used) ?? valences[valences.length - 1];
    hydrogen += Math.max(0, target - used);
  }
  return { carbon, hydrogen };
}
